import React, { useState, useEffect } from 'react';
import { FIREBASE_CONFIG } from '../config/Config';
import { logoutUser } from '../services/AuthRepository';
import { searchClients, searchProcesses } from '../services/AdminRepository';
import DocumentFactoryHub from '../components/DocumentFactoryHub'; 
import { ClientManager } from '../features/clients/components/ClientManager';
import { FinancialManager } from '../features/finance/OfficeFinance';

import { AgendaManager } from '../features/productivity/AgendaManager';      
import { TimesheetManager } from '../features/productivity/TimesheetManager';
import { PersonalFinanceManager } from '../features/finance/PersonalFinance'; 

import ProfitabilityWidget from '../components/ProfitabilityWidget'; 
import MFASetupModal from '../components/MFASetupModal'; 
import ContentManager from '../features/academy/admin/ContentManager';
import AutosDashboard from '../features/files/AutosDashboard';
import IngestionHub from '../features/files/IngestionHub';
import PortalInbox from '../features/files/PortalInbox';
import AlertInbox from '../components/alerts/AlertInbox';
import DeadlineCalculator from '../components/deadlines/DeadlineCalculator';
import { MasterSidebar } from '../components/layout/MasterSidebar';
import LibraryManager from '../features/library/LibraryManager';
import SurgeonHub from './SurgeonHub';
import { ContractFactory } from '../features/contracts/ContractEngine';
import { ProcurationFactory } from '../features/procurations/ProcurationEngine';
import { OfficeSettings } from '../features/settings/OfficeSettings';
import GlobalActivityFeed from './GlobalActivityFeed';
import PortalPreviewPage from './admin/PortalPreviewPage';
import { publishedDocumentEditEventName, readQueuedPublishedDocumentEdit } from '../utils/publishedDocumentEditor';
import { useAdminDashboard } from '../hooks/useAdminDashboard_v2';

import { DeclarationFactory } from '../features/declarations/DeclarationEngine';
import { TermoRenunciaAdvogadoFactory } from '../features/mandate_termination/TermoRenunciaAdvogadoEngine';
import { TermoRevogacaoClienteFactory } from '../features/mandate_termination/TermoRevogacaoClienteEngine';
import PetitionEngine from '../features/petitions/PetitionEngine';
import { InformedConsentFactory } from '../features/petitions/InformedConsentEngine';
import { AcademyStudio } from '../features/academy/AcademyStudio';
import InvestmentDashboard from '../features/finance/InvestmentDashboard';
import RectaScriptaDashboard from '../features/recta_scripta/RectaScriptaDashboard';
import LegalBIDashboard from '../pages/LegalBIDashboard';
import RectaFinanceDashboard from '../features/finance/RectaFinanceDashboard';

const appId = FIREBASE_CONFIG.projectId;

const OmniSearch = ({ onNavigate }) => {

    const [term, setTerm] = useState('');
    const [results, setResults] = useState({ clients: [], processes: [], docs: [] });
    const [searching, setSearching] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const delay = setTimeout(async () => {
            if (term.length < 3) { setResults({ clients: [], processes: [], docs: [] }); setIsOpen(false); return; }
            setSearching(true); setIsOpen(true);
            try {
                const clients = await searchClients(term.toLowerCase(), 5);
                const processes = await searchProcesses(term.toLowerCase(), 5);
                setResults({ clients, processes });
            } catch (e) { console.error(e); } finally { setSearching(false); }
        }, 500);
        return () => clearTimeout(delay);
    }, [term]);

    return (
        <div className="relative w-96">
            <input className="w-full pl-9 pr-4 py-2 bg-gray-100 border-transparent focus:bg-white focus:border-[#C5A059] rounded-lg text-sm outline-none" placeholder="Buscar..." value={term} onChange={e => setTerm(e.target.value)} />
            <i className={`fas ${searching ? 'fa-spinner fa-spin' : 'fa-search'} absolute left-3 top-2.5 text-gray-400 text-xs`}></i>
            {isOpen && (results.clients.length > 0 || results.processes.length > 0) && (
                <div className="absolute top-full left-0 w-full mt-2 rda-modal overflow-hidden z-50">
                    {results.clients.map(c => <div key={c.id} onClick={() => onNavigate('clientes')} className="p-2 hover:bg-blue-50 cursor-pointer text-xs">{c.title}</div>)}
                    {/* MC49: 'processos' não renderiza mais nada (ProcessManager
                        removido) — aponta pro Caderno de Autos real */}
                    {results.processes.map(p => <div key={p.id} onClick={() => onNavigate('file_hub')} className="p-2 hover:bg-green-50 cursor-pointer text-xs">{p.title}</div>)}
                </div>
            )}
        </div>
    );
};

const DashboardHome = ({ user }) => (
    <div className="space-y-6 pb-10">

        <div className="bg-white p-6 rounded-2xl shadow-md border border-gray-300">
            <h2 className="text-2xl font-serif font-bold text-[#20243B]">Bem-vindo, Dr. Rodrigo.</h2>
            <p className="text-sm text-gray-500">Visão Geral do Escritório</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <ProfitabilityWidget />
            <div className="md:col-span-2"><div className="bg-white p-6 rounded-2xl border border-gray-300 shadow-md h-full"><h3 className="font-bold text-gray-700">Agenda</h3><AgendaManager /></div></div>
        </div>

    </div>
);

export const AdminDashboard = ({ user, onNavigate }) => {
  const admin = useAdminDashboard();

  return (
    <div data-testid="admin-dashboard" className="flex h-screen bg-white font-sans overflow-hidden">
      <MasterSidebar activeTab={admin.activeTab} setActiveTab={admin.setActiveTab} user={user} logoutUser={logoutUser} onNavigate={onNavigate} />

      <main data-testid="admin-main" className="flex-1 flex flex-col h-full overflow-hidden relative bg-gray-50 print:h-auto print:overflow-visible">
        <header data-testid="admin-header" className="bg-white border-b border-gray-300 min-h-[72px] flex items-center px-8 justify-between print:hidden flex-shrink-0 sticky top-0 z-20 shadow-sm">
          <div className="flex min-w-0 flex-1 items-center gap-3">
             <div className="min-w-0 flex-shrink-0">
               <h2 data-testid="page-title" className="text-xl font-bold text-[#20243B]">{admin.headerMeta.title}</h2>
               <div data-testid="page-subtitle" className="text-xs text-gray-500">{admin.headerMeta.subtitle}</div>
             </div>
             <div className="ml-4 hidden min-w-0 flex-1 xl:block xl:max-w-[320px]"><OmniSearch onNavigate={admin.setActiveTab} /></div>
             {admin.activeTab === 'docs' && (
                 <div className="ml-6 flex bg-gray-100 p-1 rounded-lg border border-gray-300">
                     <button onClick={() => admin.setActiveTab('file_hub')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded flex items-center gap-2 ${admin.activeTab === 'file_hub' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><i className="fas fa-folder-open"></i> Anexos</button>
                     <button onClick={() => admin.setDocSubTab('UPLOADS')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded flex items-center gap-2 ${admin.docSubTab === 'UPLOADS' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><i className="fas fa-cloud-upload-alt"></i> Central de Uploads</button>
                     <button onClick={() => admin.setDocSubTab('CONTRACTS')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded flex items-center gap-2 ${admin.docSubTab === 'CONTRACTS' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><i className="fas fa-file-contract"></i> Contratos</button>
                     <button onClick={() => admin.setDocSubTab('PROCURATION')} className={`px-4 py-1.5 text-[10px] font-bold uppercase rounded flex items-center gap-2 ${admin.docSubTab === 'PROCURATION' ? 'bg-white text-[#20243B] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}><i className="fas fa-file-signature"></i> Procurações</button>
                 </div>
             )}
          </div>
          <div className="ml-4 flex flex-shrink-0 items-center gap-3">
             <button
               type="button"
               onClick={admin.handleDownloadLocalEngineInstaller}
               className="inline-flex items-center rounded border border-gray-300 bg-white px-2 py-1 text-[10px] font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700"
               title="Baixar instalador do motor local"
             >
               Instalador local
             </button>
          </div>
        </header>

        <div className={`flex-1 overflow-y-auto relative flex flex-col custom-scrollbar ${admin.isFullBleedTab ? 'p-0 bg-white' : 'p-8 bg-gray-50'}`}>
          <div className={`w-full h-full flex flex-col ${admin.isFullBleedTab ? '' : 'max-w-7xl mx-auto pb-10'}`}>
            {admin.activeTab === 'home' && <DashboardHome user={user} />}
            {admin.activeTab === 'academy_admin' && <ContentManager />}
            {admin.activeTab === 'academy_research' && <LibraryManager />}
            {admin.activeTab === 'docs' && <div className="flex-1 w-full h-full overflow-hidden"><DocumentFactoryHub activeTab={admin.docSubTab} /></div>}
            {admin.activeTab === 'documents' && <div className="flex-1 w-full h-full overflow-hidden"><ContractFactory /></div>}
            {admin.activeTab === 'contracts' && <div className="flex-1 w-full h-full overflow-hidden"><ContractFactory /></div>}
            {admin.activeTab === 'procurations' && <div className="flex-1 w-full h-full overflow-visible relative z-[80]"><ProcurationFactory /></div>}
            {admin.activeTab === 'office_uploads' && <div className="flex-1 w-full h-full overflow-hidden"><DocumentFactoryHub activeTab="UPLOADS" /></div>}
            {admin.activeTab === 'portal_inbox' && <div className="flex-1 w-full h-full overflow-hidden"><PortalInbox onOpenUploadCenter={admin.handleOpenUploadCenter} /></div>}
            {admin.activeTab === 'alert_inbox' && <div className="flex-1 w-full h-full overflow-hidden"><AlertInbox /></div>}
            {admin.activeTab === 'triage_hub' && (
              <div data-testid="page-triage-hub" className="flex-1 w-full h-full overflow-hidden flex flex-col">
                <div className="px-6 py-3 border-b border-gray-200 bg-white flex items-center gap-2 print:hidden">
                  <button
                    type="button"
                    onClick={() => admin.setTriageSubTab('PORTAL_INBOX')}
                    className={
                      'px-3 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border ' +
                      (admin.triageSubTab === 'PORTAL_INBOX'
                        ? 'bg-[#20243B] text-white border-[#20243B]'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')
                    }
                  >
                    Inbox Portal
                  </button>
                  <button
                    type="button"
                    onClick={() => admin.setTriageSubTab('INGESTION_HUB')}
                    className={
                      'px-3 py-2 rounded-lg text-[10px] font-extrabold uppercase tracking-widest border ' +
                      (admin.triageSubTab === 'INGESTION_HUB'
                        ? 'bg-[#20243B] text-white border-[#20243B]'
                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50')
                    }
                  >
                    Hub Ingestão
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-hidden">
                  {admin.triageSubTab === 'INGESTION_HUB' ? <IngestionHub mode="triage" onCreateClientFromAI={admin.handleCreateClientFromAI} /> : <PortalInbox onOpenUploadCenter={admin.handleOpenUploadCenter} />}
                </div>
              </div>
            )}
            {admin.activeTab === 'whatsapp_forensics' && <div data-testid="page-whatsapp-forensics" className="flex-1 w-full h-full overflow-hidden"><IngestionHub mode="whatsapp" onCreateClientFromAI={admin.handleCreateClientFromAI} /></div>}
            {admin.activeTab === 'surgeon_hub' && <SurgeonHub />}
            {admin.activeTab === 'office_settings' && <OfficeSettings />}
            {admin.activeTab === 'clientes' && <ClientManager />} 
            {admin.activeTab === 'prontuario_global' && <GlobalActivityFeed user={user} />}
            {/* MC49 (22/08/2026): ProcessManager.jsx removido — lia a coleção
                legada `processes`, desconectada de `autos_workspaces` desde a
                fusão (MC14); sem botão de menu apontando pra 'processos'
                desde então. Único ponto que ainda navegava pra cá era o
                clique num resultado de "processos" da busca global (abaixo),
                redirecionado agora pro Caderno de Autos (file_hub), que é a
                fonte de dado real e atual. */}
            {admin.activeTab === 'financeiro' && <FinancialManager />}
            {admin.activeTab === 'personal_finance' && <PersonalFinanceManager />} 
            {admin.activeTab === 'agenda' && <AgendaManager />}
            {admin.activeTab === 'timesheet' && <TimesheetManager />}
            {admin.activeTab === 'file_hub' && <AutosDashboard />}
            {admin.activeTab === 'deadline_calculator' && <div className="flex-1 w-full h-full overflow-hidden"><DeadlineCalculator /></div>}
            {admin.activeTab === 'declarations' && <div className="flex-1 w-full h-full overflow-visible"><DeclarationFactory /></div>}
            {admin.activeTab === 'termo_renuncia' && <div className="flex-1 w-full h-full overflow-visible"><TermoRenunciaAdvogadoFactory /></div>}
            {admin.activeTab === 'termo_revogacao' && <div className="flex-1 w-full h-full overflow-visible"><TermoRevogacaoClienteFactory /></div>}
            {/* {admin.activeTab === 'petitions' && <div className="flex-1 w-full h-full overflow-visible"><PetitionEngine /></div>} // OCULTO 31/05/2026 — redação de petições ocorre no Word + RDA.Studio */}
            {admin.activeTab === 'informed_consents' && <div className="flex-1 w-full h-full overflow-visible"><InformedConsentFactory /></div>}
            {admin.activeTab === 'academy_studio' && <div className="flex-1 w-full h-full overflow-visible"><AcademyStudio /></div>}
            {admin.activeTab === 'financial_manager' && <div className="flex-1 w-full h-full overflow-visible"><FinancialManager /></div>}
            {admin.activeTab === 'recta_finance' && <div className="flex-1 w-full h-full overflow-visible"><RectaFinanceDashboard /></div>}
            {admin.activeTab === 'legal_bi' && <div className="flex-1 w-full h-full overflow-visible"><LegalBIDashboard /></div>}
            {admin.activeTab === 'investments' && <div className="flex-1 w-full h-full overflow-visible"><InvestmentDashboard /></div>}
          </div>
        </div>

      </main>
      {admin.showMFA && <MFASetupModal user={user} onClose={() => admin.setShowMFA(false)} />}
      {admin.previewClientId && (
        <PortalPreviewPage
          clientId={admin.previewClientId}
          clientName={admin.previewClientName || ''}
          onClose={() => admin.setPreviewClientId(null)}
        />
      )}
    </div>
  );
};
export default AdminDashboard;
